/**
 * Dashboard — instance overview: counts (tenants, collections, documents),
 * KB health, and the local proxy-latency panel (p50/p95, carried over from v1).
 */

import { useQuery } from "@tanstack/react-query";
import { Building2, FolderOpen, FileText, Activity } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { analyticsApi, tenantApi, collectionApi } from "@/lib/proxy-api";
import { api, ApiException } from "@/lib/api-client";
import { useAuth } from "@/store/auth";
import { useHasInstance } from "@/lib/use-instance";
import { roleLabel } from "@/lib/tenant-workspace";

interface LatencyStats {
  count: number;
  p50: number;
  p95: number;
}

export function DashboardPage() {
  const instance = useAuth((s) => s.instance);
  const user = useAuth((s) => s.user);
  const tenantScope = useAuth((s) => s.tenantScope);
  const tenantId = tenantScope && tenantScope !== "*" ? tenantScope : undefined;

  const hasInstance = useHasInstance();
  const hasTenantScope = Boolean(tenantId);
  const tenantsQ = useQuery({ queryKey: ["tenants"], queryFn: () => tenantApi.list(), enabled: hasInstance });
  const collsQ = useQuery({ queryKey: ["collections", tenantId], queryFn: () => collectionApi.list({ tenantId }), enabled: hasInstance && hasTenantScope });
  const docsQ = useQuery({
    queryKey: ["document-analytics", tenantId],
    queryFn: () => analyticsApi.documents({ tenantId }),
    enabled: hasInstance && hasTenantScope && !isTenantKeyRequired(collsQ.error),
  });
  const latencyQ = useQuery({
    queryKey: ["proxy-latency"],
    queryFn: () => api.get<LatencyStats>("/api/analytics/proxy-latency"),
    refetchInterval: 15_000,
  });

  const tenantCount = tenantsQ.data?.business?.length ?? 0;
  const collCount = collsQ.data?.collections?.length ?? 0;
  const docCount = docsQ.data?.total_documents ?? 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dashboard"
        description={instance ? `${instance.name} · ${instance.envLabel} · ${instance.region}` : "Instance overview"}
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={Building2} label="Tenants" value={tenantsQ.isLoading ? "…" : tenantCount} />
        <StatCard icon={FolderOpen} label="Collections" value={tenantResourceValue(hasTenantScope, collsQ.isLoading, collsQ.error, collCount)} />
        <StatCard icon={FileText} label="Documents" value={tenantResourceValue(hasTenantScope, docsQ.isLoading, docsQ.error, docCount)} />
        <StatCard
          icon={Activity}
          label="Your role"
          value={roleLabel(user?.role)}
        />
      </div>

      {!hasTenantScope && user?.role === "super_admin" && (
        <Card>
          <CardContent className="py-4">
            <p className="text-sm text-muted-foreground">
              Collections and documents are tenant-scoped in ServisSearch. Select a tenant workspace to see those counts.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Proxy latency panel */}
      <Card>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">Proxy latency (this server)</p>
            <span className="text-xs text-muted-foreground">
              {latencyQ.data ? `${latencyQ.data.count} calls` : "—"}
            </span>
          </div>
          {latencyQ.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : latencyQ.data ? (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-2xl font-semibold tracking-tight">{latencyQ.data.p50}<span className="text-sm text-muted-foreground font-normal"> ms</span></p>
                <p className="text-xs text-muted-foreground">p50</p>
              </div>
              <div>
                <p className="text-2xl font-semibold tracking-tight">{latencyQ.data.p95}<span className="text-sm text-muted-foreground font-normal"> ms</span></p>
                <p className="text-xs text-muted-foreground">p95</p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No data yet.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function tenantResourceValue(
  hasTenantScope: boolean,
  isLoading: boolean,
  error: unknown,
  count: number,
): string | number {
  if (!hasTenantScope) return "Tenant scoped";
  if (isLoading) return "…";
  if (isTenantKeyRequired(error)) return "Key needed";
  return count;
}

function isTenantKeyRequired(error: unknown): boolean {
  return error instanceof ApiException && error.code === "TENANT_KEY_REQUIRED";
}

function StatCard({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Building2;
  label: string;
  value: string | number;
}) {
  return (
    <Card>
      <CardContent className="pt-5">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium text-muted-foreground">{label}</p>
          <Icon className="h-4 w-4 text-muted-foreground" />
        </div>
        <p className="mt-1 text-2xl font-semibold tracking-tight">{value}</p>
      </CardContent>
    </Card>
  );
}
